import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Typography, useTheme } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
    useModal,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    groupesTravailSelectorMain,
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';
import { FaOptionIcon, plusIcon } from '@europrocurement/l2d-icons';
import { PossibleTypes, useSearchBar } from '@europrocurement/l2d-modules';

import { useClickActions, useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-duotone-svg-icons';
import { Link } from 'react-router-dom';
import { utilisateursUtilisateursPath } from '../../constants/paths';
import GroupesTravailActionMenu from '../../components/molecule/GroupesTravailActionMenu';
import { WorkGroupCreateForm } from '../../components/GroupesTravail/WorkGroupModals/WorkGroupCreateForm';

const filters: FiltersDatatableList = [
    {
        label: 'Libellé',
        field: 'lib',
        type: 'text',
    },
    {
        label: 'Domaine',
        field: 'domaineDdmLibelle',
        type: 'text',
    },
    {
        label: 'Groupes cloturés',
        field: 'fDeleteStatus',
        type: 'boolean',
    },
];

/**
 * Page de listing des groupes de travail.
 */
const GroupesTravailList: React.FunctionComponent = function () {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const dataSource = useUtilisateursServiceSelector(groupesTravailSelectorMain);
    const { getGroupesTravail } = useUtilisateurService();
    const { isInitialLoading, isInError } = useInitialLoadingState(dataSource);
    const { navigateOnClick } = useClickActions({ navigate });

    // Activation du SearchBar pour les groupes de travail
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.WorkGroup);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    const breadcrumbPath = React.useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/utilisateurs', path: 'Gestion des utilisateurs' },
            'Liste des groupes de travail',
        ],
        [],
    );

    const columns: ColumnDatatable<GroupeTravailUtilisateurApiObject>[] = React.useMemo(
        () => [
            {
                label: 'Libellé',
                render: ({ lib }: GroupeTravailUtilisateurApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {lib}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'lib',
            },
            {
                label: 'Référent',
                render: (workgroup: GroupeTravailUtilisateurApiObject) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {workgroup.iduserReferent?.prenom} {workgroup.iduserReferent?.nom}
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            sx={{
                                ':hover': { cursor: 'pointer', color: theme.palette.primary.dark },
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <Link
                                style={{ color: 'inherit' }}
                                to={`${utilisateursUtilisateursPath}${workgroup.iduserReferent?.id}`}
                                target="_blank"
                            >
                                <FaOptionIcon icon={faArrowUpRightFromSquare} />
                            </Link>
                        </Typography>
                    </Box>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'iduserReferent',
            },
            {
                label: 'Domaine',
                render: (workgroup: GroupeTravailUtilisateurApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {workgroup.domaine?.ddmLibelle}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'domaineDdmLibelle',
            },
            {
                label: 'Mail',
                render: ({ email }: GroupeTravailUtilisateurApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {email}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'email',
            },
            {
                label: 'Téléphone',
                render: ({ phone }: GroupeTravailUtilisateurApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {phone}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'phone',
            },
            {
                label: 'Dates',
                render: (workgroup: GroupeTravailUtilisateurApiObject) => {
                    const creation = workgroup.dCreation
                        ? new Date(workgroup.dCreation).toLocaleDateString()
                        : 'Non spécifié';
                    const cloture = workgroup.dCloture
                        ? new Date(workgroup.dCloture).toLocaleDateString()
                        : 'Non spécifié';
                    return (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {`${creation} - ${cloture}`}
                        </Typography>
                    );
                },
                isDisplayed: true,
                sortable: true,
                field: 'dCreation',
            },
            {
                label: 'Actions',
                render: (workgroup: GroupeTravailUtilisateurApiObject) =>
                    workgroup.id &&
                    workgroup.iduserReferent?.id && (
                        <GroupesTravailActionMenu
                            workgroupId={workgroup.id}
                            referentId={workgroup.iduserReferent.id}
                        />
                    ),
                isDisplayed: true,
                displayLabel: false,
                rowReverse: true,
            },
        ],
        [theme.palette.primary.dark],
    );

    const handleCreateWorkGroup = React.useCallback(() => {
        modalActions.call(<WorkGroupCreateForm />);
    }, [modalActions]);

    // Chargement initial des données
    React.useEffect(() => {
        dispatch(getGroupesTravail({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowClick = React.useCallback(
        (_: React.MouseEvent<HTMLElement>, workgroup: GroupeTravailUtilisateurApiObject) => {
            navigateOnClick({ path: `${workgroup.id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        (_: React.MouseEvent<HTMLElement>, workgroup: GroupeTravailUtilisateurApiObject) => {
            navigateOnClick({ path: `${workgroup.id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Nouveau groupe de travail',
                icon: <FaOptionIcon {...plusIcon.props} />,
                action: handleCreateWorkGroup,
            },
        ],
        [handleCreateWorkGroup],
    );

    useDumbActionsContext(contextActions);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des groupes de travail."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={7} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Nouveau groupe de travail"
                        icon={plusIcon}
                        onClick={handleCreateWorkGroup}
                        actionType="modal"
                    />
                }
            />

            {dataSource && (
                <StoreDatatable
                    dataSource={dataSource}
                    columns={columns}
                    fetchData={getGroupesTravail}
                    filters={filters}
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={handleRowClick}
                    onWheelClickRow={handleRowWheelClick}
                />
            )}
        </SkeletonLoader>
    );
};

export default GroupesTravailList;
